// Bitácora del SISTEMA (contrato v2, solo el director): altas y cambios de usuarios, tablas de precios, llave, modelo y
// tope de la IA, entradas y salidas, política de acceso. Más reciente primero, con el nombre de quien lo hizo. El
// historial de CADA cotización vive en su sección Seguimiento.
import { useEffect, useState } from 'react';
import { RefreshCw, ScrollText } from 'lucide-react';
import { ACCIONES_BITACORA, ETIQUETA_ACCION_BITACORA, fechaHoraCorta } from '@cotizador/compartido';
import type { AccionBitacora, EntradaBitacora, Usuario } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { ErrorDeApi } from '../../api/cliente.ts';
import { detalleLegible } from '../../logica/ediciones.ts';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, CampoSelect, CampoTexto, Cargando, Chip, ErrorEnLinea, Seccion, Vacio } from '../../componentes/ui.tsx';

/** Cuántas entradas se piden de una vez (el servidor acepta hasta 500). */
const LIMITE = 200;

type Tono = 'neutro' | 'prusia' | 'ambar' | 'rojo' | 'verde';
/** Color del chip por acción: lo que toca dinero o la llave resalta; entrar y salir, neutro. */
const TONO_ACCION: Readonly<Record<AccionBitacora, Tono>> = {
  usuario_alta: 'prusia',
  usuario_cambio: 'prusia',
  precios: 'ambar',
  ia_llave_guardada: 'ambar',
  ia_llave_borrada: 'rojo',
  ia_tope: 'ambar',
  ia_modelo: 'ambar',
  entrada: 'neutro',
  salida: 'neutro',
  politica_acceso: 'rojo',
  correo_acceso: 'rojo',
  aprobacion: 'ambar',
  pdf_fallo: 'rojo',
  base_lecturas: 'rojo',
  generacion: 'ambar',
  papelera: 'rojo',
};

interface Filtros {
  accion: AccionBitacora | '';
  usuario_id: string;
  desde: string;
  hasta: string;
}
const SIN_FILTROS: Filtros = { accion: '', usuario_id: '', desde: '', hasta: '' };

export function Bitacora() {
  const { puede } = usarUsuario();
  const [filtros, setFiltros] = useState<Filtros>(SIN_FILTROS);
  const [entradas, setEntradas] = useState<EntradaBitacora[] | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [cargando, setCargando] = useState(false);

  const cargar = async (f: Filtros) => {
    setCargando(true);
    setError(null);
    try {
      const r = await api.bitacora({ ...f, limite: LIMITE });
      setEntradas(r.entradas);
    } catch (e) {
      setError(e);
      setEntradas(null);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    // Con "desde" después de "hasta" no se pregunta: el campo ya dice qué está mal.
    if (filtros.desde && filtros.hasta && filtros.desde > filtros.hasta) return;
    void cargar(filtros);
  }, [filtros]);

  useEffect(() => {
    api
      .usuarios()
      .then((r) => setUsuarios(r.usuarios))
      .catch(() => setUsuarios([]));
  }, []);

  if (!puede('ver_bitacora')) return <Vacio titulo="Solo Diego ve la bitácora">La bitácora del sistema es del director.</Vacio>;

  const poner = <K extends keyof Filtros>(k: K, v: Filtros[K]) => setFiltros((f) => ({ ...f, [k]: v }));
  const hayFiltros = JSON.stringify(filtros) !== JSON.stringify(SIN_FILTROS);
  const rangoMalo = filtros.desde && filtros.hasta && filtros.desde > filtros.hasta;
  // Un servidor de antes del contrato v2 no tiene la ruta: se dice en claro (y no como "no hay movimientos").
  const sinRuta = error instanceof ErrorDeApi && error.estado === 404;

  return (
    <div className="pila pila--grande">
      <Seccion
        titulo="Bitácora del sistema"
        descripcion="Usuarios, tablas de precios, llave, modelo y tope de la IA, entradas y salidas. Lo de cada cotización está en su Seguimiento."
        acciones={
          <Boton chico variante="fantasma" icono={<RefreshCw aria-hidden />} cargando={cargando} onClick={() => void cargar(filtros)}>
            Actualizar
          </Boton>
        }
      >
        <div className="hoja pila">
          <div className="filtros-bitacora">
            <CampoSelect
              etiqueta="Qué"
              value={filtros.accion}
              onChange={(e) => poner('accion', e.target.value as Filtros['accion'])}
              opciones={[{ valor: '', texto: 'Todo' }, ...ACCIONES_BITACORA.map((a) => ({ valor: a, texto: ETIQUETA_ACCION_BITACORA[a] }))]}
            />
            <CampoSelect
              etiqueta="Quién"
              value={filtros.usuario_id}
              onChange={(e) => poner('usuario_id', e.target.value)}
              opciones={[{ valor: '', texto: 'Todos' }, ...usuarios.map((u) => ({ valor: u.id, texto: u.nombre }))]}
            />
            <CampoTexto etiqueta="Desde" type="date" value={filtros.desde} onChange={(e) => poner('desde', e.target.value)} />
            <CampoTexto etiqueta="Hasta" type="date" value={filtros.hasta} onChange={(e) => poner('hasta', e.target.value)} error={rangoMalo ? 'Va después de “desde”.' : null} />
          </div>
          {hayFiltros && (
            <div className="fila-botones">
              <Boton chico variante="fantasma" onClick={() => setFiltros(SIN_FILTROS)}>
                Quitar filtros
              </Boton>
            </div>
          )}
        </div>
      </Seccion>

      {sinRuta ? (
        <div className="error-linea" role="alert">
          <div className="error-linea__cuerpo">
            <p className="error-linea__mensaje">El servidor todavía no tiene la bitácora del sistema (GET /api/bitacora).</p>
            <p className="nota-suave">Llega con la actualización del servidor del contrato v2. Lo de cada cotización sigue en su Seguimiento.</p>
          </div>
        </div>
      ) : (
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      )}

      {entradas === null && !error ? (
        <Cargando renglones={6} etiqueta="Cargando la bitácora" />
      ) : entradas !== null && entradas.length === 0 ? (
        <Vacio titulo={hayFiltros ? 'Nada con esos filtros' : 'Sin movimientos todavía'}>{hayFiltros ? 'Prueba con otras fechas o quita los filtros.' : 'Aquí aparecen los cambios de configuración y las entradas.'}</Vacio>
      ) : entradas !== null ? (
        <div className="hoja hoja--lista">
          <ol className="bitacora" aria-label="Movimientos, del más reciente al más antiguo">
            {entradas.map((x) => (
              <li key={x.id} className="bitacora__entrada">
                <time className="bitacora__cuando" dateTime={x.en}>
                  {fechaHoraCorta(x.en)}
                </time>
                <span className="bitacora__que">
                  <Chip tono={TONO_ACCION[x.accion] ?? 'neutro'}>{ETIQUETA_ACCION_BITACORA[x.accion] ?? x.accion}</Chip>
                </span>
                <span className="bitacora__quien">{x.usuario_nombre ?? (x.usuario_id ? 'Usuario borrado' : 'Sistema')}</span>
                {/* las rutas de una ampliación («/piezas/costo_bateria»), con su nombre («costo de la batería») */}
                {x.detalle && <p className="bitacora__detalle">{detalleLegible(x.detalle)}</p>}
              </li>
            ))}
          </ol>
          {entradas.length >= LIMITE && (
            <p className="nota-suave bitacora__mas">
              <ScrollText aria-hidden /> Se ven los {LIMITE} más recientes: usa las fechas para ver más atrás.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
